import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import {
  createMarketingNote,
  deleteMarketingNote,
  fetchMarketingNotes,
  updateMarketingNote,
  type MarketingNotePatch,
  type MarketingNotesPage,
  type MarketingNotesQuery,
  type NoteKind,
} from '../../../../api/venue-marketing';
import { ROSTER_KEY } from '../roster/venues-roster-api';
import { VENUE_TASK_COUNT_KEY } from '../use-venue-task-count';
import { venueKey } from '../useVenue';

import { isAccessRefusal } from './venue-marketing-errors';

/** The two lists on the tab: action items still to do, and everything else. */
export type NoteList = 'open' | 'history';

/** Sits under the venue's own key, so refreshing the venue refreshes its notes too. */
export const notesKey = (venueId: string) => [...venueKey(venueId), 'marketing-notes'] as const;

const queryFor = (list: NoteList, page: number): MarketingNotesQuery =>
  list === 'open' ? { kind: 'ACTION_ITEM', status: 'open', page } : { view: 'history', page };

const hasMore = (last: MarketingNotesPage) => last.page * last.pageSize < last.total;

/** One list of notes, newest first, loaded a page at a time. */
export function useMarketingNotes(venueId: string, list: NoteList) {
  return useInfiniteQuery({
    queryKey: [...notesKey(venueId), list],
    queryFn: ({ pageParam }) => fetchMarketingNotes(venueId, queryFor(list, pageParam)),
    initialPageParam: 1,
    getNextPageParam: (last) => (hasMore(last) ? last.page + 1 : undefined),
    enabled: venueId.length > 0,
  });
}

/** Every outcome refreshes both lists, a failure included: a 404 means the note
 *  is already gone, and a 403 or 409 means the list is stale. A 403 or 404 also
 *  reloads the venue so the caller's edit flags are current. Whatever the
 *  outcome, the task counts elsewhere may have moved, so the venues roster
 *  (by prefix) and the sidebar badge refresh too. */
function settle(qc: QueryClient, venueId: string, err: unknown) {
  void qc.invalidateQueries({ queryKey: notesKey(venueId) });
  void qc.invalidateQueries({ queryKey: ROSTER_KEY });
  void qc.invalidateQueries({ queryKey: VENUE_TASK_COUNT_KEY });
  if (isAccessRefusal(err)) void qc.invalidateQueries({ queryKey: venueKey(venueId) });
}

export function useCreateMarketingNote(venueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: NoteKind; body: string }) => createMarketingNote(venueId, input),
    onSettled: (_data, err) => settle(qc, venueId, err),
  });
}

export function useUpdateMarketingNote(venueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { noteId: string; patch: MarketingNotePatch }) =>
      updateMarketingNote(venueId, v.noteId, v.patch),
    onSettled: (_data, err) => settle(qc, venueId, err),
  });
}

export function useDeleteMarketingNote(venueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => deleteMarketingNote(venueId, noteId),
    onSettled: (_data, err) => settle(qc, venueId, err),
  });
}
